import { Tenant } from './index';

export enum FormFieldType {
  INPUT = 'input',
  SELECT = 'select',
  RADIO = 'radio',
  CHECKBOX = 'checkbox',
  FIELD_ARRAY = 'field_array',
  SCALE_RATING = 'scale_rating',
  OUTCOMES = 'outcomes',
  OUTCOME_FIELDS = 'outcome_fields',
  CSV_LOOKUP = 'csv_lookup',
  HEADING = 'heading',
  TEXT = 'text',
  PAGE_BREAK = 'page_break',
}

// UI-only value used by choice controls; it is never submitted.
export const FORM_OTHER_OPTION_VALUE =
  '__form_other_option__:806ec086-450f-4e3f-9a2f-e49cd1db0068';

export enum FormChoiceResponseType {
  OPTION = 'option',
  OTHER = 'other',
}

export interface FormChoiceResponse {
  value: string;
  type: FormChoiceResponseType;
}

export enum InputType {
  Text = 'text',
  Number = 'number',
  Email = 'email',
  Date = 'date',
}

export enum FieldArrayLayout {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum CsvLookupPreset {
  SCHOOL_LIST = 'school_list',
}

export interface InputOptions {
  type: InputType;
  maxValue?: number;
  minValue?: number;
  integer?: boolean;
  maxLength?: number;
  minLength?: number;
  multiline?: boolean;
}

export interface SelectOptions {
  options: string[];
  multiple?: boolean;
  randomize?: boolean;
  allowOther?: boolean;
  explanation?: string;
  answerValues?: string[];
  otherAnswerValue?: string;
  correctOptions?: number[];
}

export interface RadioOptions {
  options: string[];
  randomize?: boolean;
  allowOther?: boolean;
  answerValues?: string[];
  otherAnswerValue?: string;
}

export interface CheckboxOptions {
  options: string[];
  randomize?: boolean;
  allowOther?: boolean;
  answerValues?: string[];
  otherAnswerValue?: string;
}

export interface ScaleRatingOptions {
  minValue: number;
  maxValue: number;
  options: string[];
  /** Codes aligned with matrix rows in options; not translated. */
  answerCodes?: string[];
  /** Builder-only state; removed when preparing the API payload. */
  _rowsEnabled?: boolean;
  valueLabels?: string[];
  /** Export values aligned with scale positions; not translated. */
  answerValues?: string[];
}

export interface FieldArrayOptions {
  fields: FormField[];
  layout?: FieldArrayLayout;
}

export interface CsvLookupOptions {
  sourceFile?: File;
  datasetId?: string;
  itemsFormId?: string;
  preset: CsvLookupPreset;
}

export interface CsvLookupReferenceValue {
  referenceItemId: string;
}

export interface CsvLookupStoredValue extends CsvLookupReferenceValue {
  datasetId: string;
  searchValue: string;
  displayValue: string;
  snapshot: Record<string, string | null>;
}

export interface OutcomesFieldOption {
  id: string;
  label: string;
  description?: string;
}

export interface OutcomesFieldCondition {
  outcomeIds: string[];
  matchingValue: string;
}

export interface OutcomesFieldOptions {
  csvContent?: string;
  conditionValue: string;
  outcomes: OutcomesFieldOption[];
  conditions: OutcomesFieldCondition[];
}

export interface OutcomesFieldValue {
  orderedIds: string[];
  outcomes: OutcomesFieldOption[];
}

export interface FormFieldSource {
  stepId: string;
  fieldId: string;
  stepName: string;
  fieldLabel: string;
  fieldType: 'outcomes';
}

export interface OutcomeFieldsOptions {
  fields: FormField[];
  sourceFieldId: string;
  maxOutcomeCount?: number;
  sourceCourseStepId: string;
}

export type OutcomeFieldsFieldValue = Record<string, FormSubmissionData>;

export type FormFieldTranslation = {
  label: string;
  text?: string;
  subtitle?: string;
  placeholder?: string;
};

export type FormFieldTranslationWith<T> = FormFieldTranslation & T;

interface BaseFormField {
  id: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  referenceCode?: string;
  inputId?: string | number;
}

export interface InputFormField extends BaseFormField {
  options?: InputOptions;
  type: FormFieldType.INPUT;
  translations?: Record<string, FormFieldTranslation>;
}

export interface RadioFormField extends BaseFormField {
  options?: RadioOptions;
  type: FormFieldType.RADIO;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<RadioOptions, 'options'>>
  >;
}

export interface CheckboxFormField extends BaseFormField {
  options?: CheckboxOptions;
  type: FormFieldType.CHECKBOX;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<CheckboxOptions, 'options'>>
  >;
}

export interface SelectFormField extends BaseFormField {
  options?: SelectOptions;
  type: FormFieldType.SELECT;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<SelectOptions, 'options' | 'explanation'>>
  >;
}

export interface ScaleRatingFormField extends BaseFormField {
  options?: ScaleRatingOptions;
  type: FormFieldType.SCALE_RATING;
  translations?: Record<
    string,
    FormFieldTranslationWith<
      Pick<ScaleRatingOptions, 'options' | 'valueLabels'>
    >
  >;
}

export interface FieldArrayFormField extends BaseFormField {
  options?: FieldArrayOptions;
  type: FormFieldType.FIELD_ARRAY;
}

export interface OutcomesFormField extends BaseFormField {
  type: FormFieldType.OUTCOMES;
  options?: OutcomesFieldOptions;
}

export interface OutcomeFieldsFormField extends BaseFormField {
  options?: OutcomeFieldsOptions;
  type: FormFieldType.OUTCOME_FIELDS;
}

export interface PageBreakFormField extends BaseFormField {
  options?: {};
  type: FormFieldType.PAGE_BREAK;
}
export interface TextFormField extends Omit<BaseFormField, 'placeholder'> {
  text: string;
  type: FormFieldType.TEXT;
  options?: Record<string, never>;
  translations?: Record<
    string,
    Omit<FormFieldTranslation, 'subtitle' | 'placeholder'>
  >;
}

export interface HeadingFormField extends Omit<BaseFormField, 'placeholder'> {
  subtitle?: string;
  type: FormFieldType.HEADING;
  options?: Record<string, never>;
  translations?: Record<string, Omit<FormFieldTranslation, 'placeholder'>>;
}
export interface CsvLookupFormField extends BaseFormField {
  options: CsvLookupOptions;
  type: FormFieldType.CSV_LOOKUP;
}

export type FormField =
  | TextFormField
  | InputFormField
  | RadioFormField
  | SelectFormField
  | HeadingFormField
  | CheckboxFormField
  | OutcomesFormField
  | PageBreakFormField
  | CsvLookupFormField
  | FieldArrayFormField
  | ScaleRatingFormField
  | OutcomeFieldsFormField;

export interface FormBlock {
  id: string;
  name?: string;
  fields: FormField[];
}

export interface FormData {
  blocks: FormBlock[];
}

export interface FormSubmissionData {
  [fieldId: string]:
    | string
    | number
    | string[]
    | number[]
    | FormChoiceResponse
    | OutcomesFieldValue
    | FormSubmissionData
    | FormChoiceResponse[]
    | CsvLookupStoredValue[]
    | OutcomeFieldsFieldValue
    | CsvLookupReferenceValue[];
}

export interface FormSubmission {
  id: string;
  userId: string;
  formId: string;
  createdAt: string;
  updatedAt: string;
  data: FormSubmissionData;
}

export interface ValidationError {
  fieldId: string;
  message: string;
}

export interface Form {
  id: string;
  data: FormData;
  version: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  tenantId?: string | null;
  courseStepId: string | null;
  tenant?: Pick<Tenant, 'name'>;
}

export const FormFieldTypeTitle = {
  [FormFieldType.TEXT]: 'Text',
  [FormFieldType.INPUT]: 'Input',
  [FormFieldType.RADIO]: 'Radio',
  [FormFieldType.SELECT]: 'Select',
  [FormFieldType.HEADING]: 'Heading',
  [FormFieldType.CHECKBOX]: 'Checkbox',
  [FormFieldType.OUTCOMES]: 'Outcomes',
  [FormFieldType.CSV_LOOKUP]: 'CSV Lookup',
  [FormFieldType.PAGE_BREAK]: 'Page Break',
  [FormFieldType.FIELD_ARRAY]: 'Field Array',
  [FormFieldType.SCALE_RATING]: 'Scale Rating',
  [FormFieldType.OUTCOME_FIELDS]: 'Outcome Fields',
};
